import { ContentReportCategory } from "Infrastructure/Api/Api";
import { Resources, type TFunction } from "Translations/Resources";
import { z } from "zod";

// Must stay in sync with CreateContentReportCommand.ExplanationMinLength / MaxLength.
export const CONTENT_REPORT_EXPLANATION_MIN_LENGTH = 20;
export const CONTENT_REPORT_EXPLANATION_MAX_LENGTH = 2000;
export const CONTENT_REPORT_NAME_MAX_LENGTH = 100;

const RESOURCES = Resources.Support.ContentReport.Validation;

type SchemaOptions = {
  /** A signed-in reporter is identified by the server from the token; only an anonymous one
      fills in name and e-mail. */
  isAnonymous: boolean;
  /** The anonymous Support-picker path names the profile by handle; the profile page and the
      chat already know the target and hide the field. */
  isHandleRequired: boolean;
};

/**
 * The reporter's contact is mandatory for an anonymous caller EXCEPT for a child sexual abuse
 * notice (DSA Art. 16(2)(c)), so its requiredness depends on the category and has to be a
 * whole-object check rather than a field schema. The errors are attached to the fields so they
 * show up in place like any other validation message.
 */
export const getContentReportSchema = (
  t: TFunction,
  { isAnonymous, isHandleRequired }: SchemaOptions,
) =>
  z
    .object({
      handle: z
        .string()
        .trim()
        .refine((handle) => !isHandleRequired || !!handle, {
          error: t(RESOURCES.HandleRequired),
        }),
      category: z.enum(ContentReportCategory, {
        error: t(RESOURCES.CategoryRequired),
      }),
      explanation: z
        .string()
        .trim()
        .min(1, { error: t(RESOURCES.ExplanationRequired) })
        .min(CONTENT_REPORT_EXPLANATION_MIN_LENGTH, {
          error: t(RESOURCES.ExplanationTooShort, {
            minLength: CONTENT_REPORT_EXPLANATION_MIN_LENGTH,
          }),
        })
        .max(CONTENT_REPORT_EXPLANATION_MAX_LENGTH),
      name: z.string().trim().max(CONTENT_REPORT_NAME_MAX_LENGTH),
      email: z.string().trim(),
      isBonaFideConfirmed: z.boolean().refine((isConfirmed) => isConfirmed, {
        error: t(RESOURCES.BonaFideRequired),
      }),
    })
    .superRefine(({ category, name, email }, context) => {
      const isContactRequired =
        isAnonymous && category !== ContentReportCategory.ChildSexualAbuse;

      if (!isContactRequired) {
        if (email && !z.email().safeParse(email).success) {
          context.addIssue({
            code: "custom",
            path: ["email"],
            message: t(RESOURCES.EmailInvalid),
          });
        }
        return;
      }

      if (!name) {
        context.addIssue({
          code: "custom",
          path: ["name"],
          message: t(RESOURCES.NameRequired),
        });
      }

      if (!email) {
        context.addIssue({
          code: "custom",
          path: ["email"],
          message: t(RESOURCES.EmailRequired),
        });
      } else if (!z.email().safeParse(email).success) {
        context.addIssue({
          code: "custom",
          path: ["email"],
          message: t(RESOURCES.EmailInvalid),
        });
      }
    });

export type ContentReportFormData = z.infer<
  ReturnType<typeof getContentReportSchema>
>;
